import ExcelJS from 'exceljs';
import { format } from 'date-fns';

/**
 * Style constants for Work Order reports (purple theme)
 */
const COLORS = {
  headerBg: 'FF7C3AED', // Purple-600
  headerText: 'FFFFFFFF',
  titleBg: 'FF581C87', // Purple-900
  titleText: 'FFFFFFFF',
  totalsBg: 'FFF3E8FF', // Purple-50
  totalsText: 'FF581C87',
  borderColor: 'FFD8B4FE', // Purple-300
  profitPositive: 'FF16A34A', // Green-600
  profitNegative: 'FFDC2626', // Red-600
  expenseBg: 'FFF5F3FF', // Purple-50 lighter
  expenseHeaderBg: 'FFE9D5FF', // Purple-200
  workOrderBg: 'FFFFFFFF', // White
  laborBg: 'FFDBEAFE', // Blue-100
  laborHeaderBg: 'FF93C5FD', // Blue-300
  laborText: 'FF1E40AF', // Blue-800
};

/**
 * Apply title row styling
 */
const applyTitleRowStyle = (row) => {
  if (!row) return;
  row.height = 28;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.titleBg }
    };
    cell.font = { bold: true, size: 16, color: { argb: COLORS.titleText } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
};

/**
 * Apply header row styling
 */
const applyHeaderRowStyle = (row) => {
  if (!row) return;
  row.height = 24;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.headerBg }
    };
    cell.font = { bold: true, size: 11, color: { argb: COLORS.headerText } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
      bottom: { style: 'thin', color: { argb: COLORS.borderColor } }
    };
  });
};

/**
 * Apply expense header row styling
 */
const applyExpenseHeaderRowStyle = (row) => {
  if (!row) return;
  row.height = 20;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.expenseHeaderBg }
    };
    cell.font = { bold: true, size: 10, color: { argb: 'FF581C87' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
};

/**
 * Apply expense row styling
 */
const applyExpenseRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.expenseBg }
    };
    cell.font = { size: 10, color: { argb: 'FF6B21A8' } };
  });
};

/**
 * Apply labor header row styling
 */
const applyLaborHeaderRowStyle = (row) => {
  if (!row) return;
  row.height = 20;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.laborHeaderBg }
    };
    cell.font = { bold: true, size: 10, color: { argb: COLORS.laborText } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
};

/**
 * Apply labor row styling
 */
const applyLaborRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.laborBg }
    };
    cell.font = { size: 10, color: { argb: COLORS.laborText } };
  });
};

/**
 * Apply totals row styling
 */
const applyTotalsRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.totalsBg }
    };
    cell.font = { bold: true, size: 11, color: { argb: COLORS.totalsText } };
    cell.border = {
      top: { style: 'thin', color: { argb: COLORS.borderColor } }
    };
  });
};

/**
 * Apply work order row styling (light background)
 */
const applyWorkOrderRowStyle = (row, hasExpenses = false) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.font = { bold: hasExpenses, size: 11 };
    if (hasExpenses) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF9FAFB' } // Gray-50
      };
    }
  });
};

/**
 * Apply info row styling (for filter info)
 */
const applyInfoRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } };
  });
};

/**
 * Format currency for display
 */
const formatCurrency = (value) => {
  const numValue = Number(value || 0);
  if (isNaN(numValue)) return 0;
  return Number(numValue.toFixed(2));
};

/**
 * Apply currency formatting to specific cells in a row
 */
const applyCurrencyToRow = (row, columnIndexes) => {
  if (!row || !Array.isArray(columnIndexes)) return;
  columnIndexes.forEach(index => {
    const cell = row.getCell(index);
    if (cell) {
      const currentValue = typeof cell.value === 'number' ? cell.value : Number(cell.value || 0);
      if (!isNaN(currentValue)) {
        cell.value = formatCurrency(currentValue);
      }
      cell.numFmt = '$#,##0.00';
    }
  });
};

/**
 * Format date safely - handles YYYY-MM-DD strings as local time
 */
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    // If it's a YYYY-MM-DD string, add T00:00:00 to parse as local time (prevents timezone shift)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return format(new Date(dateValue + 'T00:00:00'), 'MMM dd, yyyy');
    }
    return format(new Date(dateValue), 'MMM dd, yyyy');
  } catch {
    return '';
  }
};

/**
 * Format date with time
 */
const formatDateTime = (dateValue) => {
  if (!dateValue) return '';
  try {
    return format(new Date(dateValue), 'MMM dd, yyyy HH:mm');
  } catch {
    return '';
  }
};

/**
 * Status labels for display
 */
const STATUS_LABELS = {
  COMPLETED: 'Completed',
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  CANCELED: 'Canceled'
};

/**
 * Format time in hours and minutes
 */
const formatTimeWorked = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return '0h 0m';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

/**
 * Download Excel file
 */
const downloadExcel = (buffer, filename) => {
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Export Work Orders report to Excel
 */
export const exportWorkOrdersToExcel = async (reportData, metadata = {}) => {
  if (!reportData || !reportData.workOrders) {
    throw new Error('Invalid report data');
  }

  const { workOrders, summary } = reportData;
  const { 
    startDate, 
    endDate, 
    appliedAreas = [], 
    appliedDepartments = [], 
    appliedStatuses = [] 
  } = metadata;

  // Create workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sweetgrass Properties';
  workbook.created = new Date();

  // ===== SHEET 1: Work Orders with Expenses =====
  const wsWorkOrders = workbook.addWorksheet('Work Orders');

  // Define columns for work orders
  const columns = [
    { header: 'Property', key: 'property', width: 30 },
    { header: 'Description', key: 'description', width: 40 },
    { header: 'Area', key: 'area', width: 15 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Department', key: 'department', width: 20 },
    { header: 'Assigned To', key: 'assignedTo', width: 18 },
    { header: 'Base Cost', key: 'baseCost', width: 14 },
    { header: 'Billed Amount', key: 'billedAmount', width: 14 },
    { header: 'Profit', key: 'profit', width: 14 },
    { header: 'Submitted', key: 'submitted', width: 14 }
  ];

  // Set column widths
  columns.forEach((col, idx) => {
    wsWorkOrders.getColumn(idx + 1).width = col.width;
  });

  // Title row
  const dateRangeStr = startDate && endDate 
    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
    : formatDate(new Date());
  const titleRow = wsWorkOrders.addRow([`Work Orders Report - ${dateRangeStr}`]);
  wsWorkOrders.mergeCells(titleRow.number, 1, titleRow.number, columns.length);
  applyTitleRowStyle(titleRow);

  // Generated timestamp
  wsWorkOrders.addRow([`Generated: ${formatDateTime(new Date())}`]);

  // Filter information
  if (appliedStatuses.length > 0) {
    const statusRow = wsWorkOrders.addRow([`Statuses: ${appliedStatuses.map(s => STATUS_LABELS[s] || s).join(', ')}`]);
    applyInfoRowStyle(statusRow);
  }
  if (appliedDepartments.length > 0) {
    const deptRow = wsWorkOrders.addRow([`Departments: ${appliedDepartments.join(', ')}`]);
    applyInfoRowStyle(deptRow);
  }
  if (appliedAreas.length > 0) {
    const areaRow = wsWorkOrders.addRow([`Areas: ${appliedAreas.map(a => a.replace(/_/g, ' ')).join(', ')}`]);
    applyInfoRowStyle(areaRow);
  }

  // Empty row before data
  wsWorkOrders.addRow([]);

  // Header row
  const headerRow = wsWorkOrders.addRow(columns.map(c => c.header));
  applyHeaderRowStyle(headerRow);

  // Data rows with inline expenses
  workOrders.forEach(wo => {
    const hasExpenses = wo.expenses && wo.expenses.length > 0;
    const profit = (wo.totalBilledAmount || 0) - (wo.totalBaseCost || 0);
    
    // Work order row
    const dataRow = wsWorkOrders.addRow([
      wo.propertyName || '',
      wo.description || '',
      wo.propertyArea || '',
      STATUS_LABELS[wo.status] || wo.status,
      wo.assignedDepartment || '',
      wo.assignedToName || 'Unassigned',
      formatCurrency(wo.totalBaseCost),
      formatCurrency(wo.totalBilledAmount),
      formatCurrency(profit),
      formatDate(wo.submittedAt)
    ]);
    
    // Wrap text for description column
    dataRow.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    
    // Apply styling
    applyWorkOrderRowStyle(dataRow, hasExpenses);
    applyCurrencyToRow(dataRow, [7, 8, 9]);
    
    // Color profit cell based on value
    const profitCell = dataRow.getCell(9);
    if (profit >= 0) {
      profitCell.font = { ...profitCell.font, color: { argb: COLORS.profitPositive } };
    } else {
      profitCell.font = { ...profitCell.font, color: { argb: COLORS.profitNegative } };
    }

    // Add expenses if any
    if (hasExpenses) {
      // Expense header row
      const expHeaderRow = wsWorkOrders.addRow([
        '  ↳ Expenses:',
        '',
        'Title',
        '',
        '',
        'Added By',
        'Base Cost',
        'Billed Amount',
        'Profit',
        'Date'
      ]);
      applyExpenseHeaderRowStyle(expHeaderRow);

      // Individual expense rows
      wo.expenses.forEach(expense => {
        const expBaseCost = expense.baseCost || 0;
        const expBilledAmount = expense.billedAmount || 0;
        const expProfit = expBilledAmount - expBaseCost;

        const expRow = wsWorkOrders.addRow([
          '',
          '',
          expense.title || '',
          '',
          '',
          expense.createdByName || '',
          formatCurrency(expBaseCost),
          formatCurrency(expBilledAmount),
          formatCurrency(expProfit),
          formatDate(expense.createdAt)
        ]);
        
        applyExpenseRowStyle(expRow);
        applyCurrencyToRow(expRow, [7, 8, 9]);

        // Color expense profit
        const expProfitCell = expRow.getCell(9);
        if (expProfit >= 0) {
          expProfitCell.font = { size: 10, color: { argb: COLORS.profitPositive } };
        } else {
          expProfitCell.font = { size: 10, color: { argb: COLORS.profitNegative } };
        }
      });
    }

    // Add labor charges if time was logged
    const hasLabor = wo.totalTimeWorkedMinutes && wo.totalTimeWorkedMinutes > 0;
    if (hasLabor) {
      // Labor header row
      const laborHeaderRow = wsWorkOrders.addRow([
        '  ↳ Labor:',
        '',
        'Time Logged',
        'Billable Hours',
        '',
        'Rate',
        '',
        'Labor Charge',
        '',
        ''
      ]);
      applyLaborHeaderRowStyle(laborHeaderRow);

      // Labor data row
      const hourlyRate = summary?.serviceChargeHourlyRate || 0;
      const laborRow = wsWorkOrders.addRow([
        '',
        '',
        formatTimeWorked(wo.totalTimeWorkedMinutes),
        `${(wo.billableHours || 0).toFixed(2)}h`,
        '',
        hourlyRate > 0 ? `$${hourlyRate.toFixed(2)}/hr` : 'Not set',
        '',
        formatCurrency(wo.laborCharge || 0),
        '',
        ''
      ]);
      applyLaborRowStyle(laborRow);
      applyCurrencyToRow(laborRow, [8]);

      // Add note about first hour free
      if (wo.totalTimeWorkedMinutes >= 60) {
        const noteRow = wsWorkOrders.addRow([
          '',
          '',
          '(First hour included free)',
          '',
          '',
          '',
          '',
          '',
          '',
          ''
        ]);
        noteRow.getCell(3).font = { italic: true, size: 9, color: { argb: 'FF6B7280' } };
      }
    }

    // Small spacer row after details
    if (hasExpenses || hasLabor) {
      wsWorkOrders.addRow([]);
    }
  });

  // Grand Totals row
  const totalLaborCharge = summary.totalLaborCharge || 0;
  const totalBilledWithLabor = (summary.grandTotalBilledAmount || 0);
  const totalProfit = totalBilledWithLabor - (summary.grandTotalBaseCost || 0);
  
  wsWorkOrders.addRow([]); // Empty row before totals
  const totalsRow = wsWorkOrders.addRow([
    'GRAND TOTALS',
    '',
    '',
    '',
    '',
    `${workOrders.length} Work Orders`,
    formatCurrency(summary.grandTotalBaseCost),
    formatCurrency(totalBilledWithLabor),
    formatCurrency(totalProfit),
    ''
  ]);
  applyTotalsRowStyle(totalsRow);
  applyCurrencyToRow(totalsRow, [7, 8, 9]);
  
  // Color total profit
  const totalProfitCell = totalsRow.getCell(9);
  totalProfitCell.font = { 
    bold: true, 
    color: { argb: totalProfit >= 0 ? COLORS.profitPositive : COLORS.profitNegative } 
  };

  // Labor totals row (if there's any labor)
  const approvedLabor = summary.approvedLaborCharge || 0;
  if (approvedLabor > 0 || totalLaborCharge > 0) {
    const laborTotalsRow = wsWorkOrders.addRow([
      '  Labor Included:',
      '',
      `${formatTimeWorked(summary.totalTimeWorkedMinutes || 0)} logged`,
      `${(summary.totalBillableHours || 0).toFixed(2)}h billable`,
      '',
      `@ $${(summary.serviceChargeHourlyRate || 0).toFixed(2)}/hr`,
      '',
      formatCurrency(approvedLabor),
      '',
      ''
    ]);
    applyLaborRowStyle(laborTotalsRow);
    applyCurrencyToRow(laborTotalsRow, [8]);
  }

  // ===== SHEET 2: Summary =====
  const wsSummary = workbook.addWorksheet('Summary');
  
  wsSummary.getColumn(1).width = 25;
  wsSummary.getColumn(2).width = 20;

  // Title
  const summaryTitle = wsSummary.addRow(['Report Summary']);
  applyTitleRowStyle(summaryTitle);
  wsSummary.mergeCells(summaryTitle.number, 1, summaryTitle.number, 2);

  wsSummary.addRow([]);

  // Counts section
  const countsHeader = wsSummary.addRow(['Work Order Counts', '']);
  applyHeaderRowStyle(countsHeader);
  wsSummary.mergeCells(countsHeader.number, 1, countsHeader.number, 2);

  wsSummary.addRow(['Total Work Orders', summary.totalWorkOrders || 0]);
  wsSummary.addRow(['Completed', summary.completedCount || 0]);
  wsSummary.addRow(['Open', summary.openCount || 0]);
  wsSummary.addRow(['In Progress', summary.inProgressCount || 0]);
  wsSummary.addRow(['Canceled', summary.canceledCount || 0]);
  wsSummary.addRow(['Urgent', summary.urgentCount || 0]);
  wsSummary.addRow(['Feedback', summary.feedbackCount || 0]);

  // Expense counts
  const totalExpenseCount = workOrders.reduce((sum, wo) => sum + (wo.expenses?.length || 0), 0);
  const workOrdersWithExpenses = workOrders.filter(wo => wo.expenses && wo.expenses.length > 0).length;
  wsSummary.addRow([]);
  wsSummary.addRow(['Total Expenses', totalExpenseCount]);
  wsSummary.addRow(['Work Orders with Expenses', workOrdersWithExpenses]);

  wsSummary.addRow([]);

  // Financial section
  const finHeader = wsSummary.addRow(['Financial Summary', '']);
  applyHeaderRowStyle(finHeader);
  wsSummary.mergeCells(finHeader.number, 1, finHeader.number, 2);

  const baseCostRow = wsSummary.addRow(['Total Base Cost', formatCurrency(summary.grandTotalBaseCost)]);
  applyCurrencyToRow(baseCostRow, [2]);
  
  // Expense billed (without labor or service charges)
  const expBilledRow = wsSummary.addRow(['Total Expenses Billed', formatCurrency(summary.totalBilledExpenses || 0)]);
  applyCurrencyToRow(expBilledRow, [2]);
  
  // Labor section
  if (summary.totalLaborCharge > 0) {
    wsSummary.addRow([]);
    const laborHeader = wsSummary.addRow(['Labor Summary', '']);
    applyLaborHeaderRowStyle(laborHeader);
    wsSummary.mergeCells(laborHeader.number, 1, laborHeader.number, 2);
    
    wsSummary.addRow(['Total Time Logged', formatTimeWorked(summary.totalTimeWorkedMinutes || 0)]);
    wsSummary.addRow(['Total Billable Hours', `${(summary.totalBillableHours || 0).toFixed(2)} hours`]);
    wsSummary.addRow(['Hourly Rate', `$${(summary.serviceChargeHourlyRate || 0).toFixed(2)}/hr`]);
    
    const laborChargeRow = wsSummary.addRow(['Total Labor Billed', formatCurrency(summary.approvedLaborCharge || 0)]);
    applyCurrencyToRow(laborChargeRow, [2]);
    applyLaborRowStyle(laborChargeRow);

    const unbillableLabor = (summary.totalLaborCharge || 0) - (summary.approvedLaborCharge || 0);
    if (unbillableLabor > 0) {
      const unbillableLaborRow = wsSummary.addRow(['Total Labor (Unbillable)', formatCurrency(unbillableLabor)]);
      applyCurrencyToRow(unbillableLaborRow, [2]);
    }
  }

  wsSummary.addRow([]);
  
  // Total Billed (including labor)
  const totalBilledRow = wsSummary.addRow(['Total Billed (Expenses + Labor)', formatCurrency(summary.grandTotalBilledAmount)]);
  applyCurrencyToRow(totalBilledRow, [2]);
  totalBilledRow.getCell(1).font = { bold: true };
  totalBilledRow.getCell(2).font = { bold: true };
  
  const profitRow = wsSummary.addRow(['Total Profit', formatCurrency(totalProfit)]);
  applyCurrencyToRow(profitRow, [2]);
  applyTotalsRowStyle(profitRow);
  profitRow.getCell(2).font = { 
    bold: true, 
    color: { argb: totalProfit >= 0 ? COLORS.profitPositive : COLORS.profitNegative } 
  };

  // Generate filename
  const dateStr = format(new Date(), 'yyyy-MM-dd-HHmmss');
  const filename = `work-orders-report-${dateStr}.xlsx`;

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
};

export default exportWorkOrdersToExcel;
